import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import AdaptiveLogo from '../../../../common/controls/AdaptiveLogo';
import { SmartContext } from '../../../../library/Core/SmartContext';
import WorkSimilarAlumni from '../controls/WorkSimilarAlumni';
import ExpandableDescription from '../controls/ExpandableDescription';
import SessionContext from '../../../../library/Core/SessionContext';
import { getCompanyImage } from '../../../../common/styles/Images';
import { allowPerformAction, getActionElementClass, getDomainValueForCode, getFlexColDomainValueForCode, isEmpty, syncProfileMarker, formatDesignationDisplay } from '../../../../library/Core/SmartFunctions';
import { Tooltip } from 'bootstrap';
import StudentService from '../../../../services/student.service';
import { getProfileView } from '../service/student-profile-view.service';

const ProfileWorkExperience = () => {
    const { state, dispatch } = useContext(SmartContext);
    const { sessionState } = useContext(SessionContext);
    const model = state.internal.model;
    const navigate = useNavigate();
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);  
    const [isActive, setIsActive] = useState(false);
    const [isHidden, setIsHidden] = useState(false);
    const [actionBtnStatus, setActionBtnStatus]: any = useState({});
    const [selectedStatus,setSelectedStatus] :any =useState({})
    const [isDeleting, setIsDeleting] = useState(false);

    const handleClick = (id: any) => {
        setIsActive(!isActive);
        setIsHidden(!isHidden);

        setActionBtnStatus({
            ...actionBtnStatus,
            [id]: ! actionBtnStatus[id]
        });
    };

    const handleDeleteWork = async (
        workId: any,
        details: { companyName?: string; designation?: string } = {},
        source: 'workExperience' | 'campus' = 'workExperience'
    ) => {
        if (!allowPerformAction() || isDeleting) return;

        const escapeHtml = (value: string) =>
            value
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/"/g, '&quot;');

        const companyName = escapeHtml(details.companyName || '');
        const designation = escapeHtml(details.designation || '');
        const detailLines = [designation, companyName].filter(Boolean).join('<br/>');

        const result = await Swal.fire({
            title: 'Are you sure?',
            html: detailLines
                ? `<p>Do you really want to delete this ${source === 'campus' ? 'campus placement record' : 'work experience'}?</p>
                   <p style="margin-top:12px;font-weight:600;">${detailLines}</p>`
                : `Do you really want to delete this ${source === 'campus' ? 'campus placement record' : 'work experience'}?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!',
            cancelButtonText: 'Cancel',
        });

        if (!result.isConfirmed) return;

        try {
            setIsDeleting(true);
            await StudentService.deleteJob(model.id, workId);

            const updatedJobsList = (state.data.jobsList || []).filter((job: any) => String(job.id) !== String(workId));
            const updatedJobs = (state.data.jobs || []).filter((job: any) => String(job.id) !== String(workId));
            const updatedJobsApplied = (state.data.jobsApplied || []).filter((job: any) => String(job.id) !== String(workId));
            const stillCurrent = updatedJobsList.filter((job: any) => {
                const recordType = job?.recordTypeCode ?? job?.record_type_code;
                const current = job?.isCurrent === true || job?.isCurrent === 1 || job?.isCurrent === '1';
                return current && (recordType == null || recordType === 'JOINED');
            });
            const updatedData = {
                ...state.data,
                jobsList: updatedJobsList,
                jobs: updatedJobs,
                jobsApplied: updatedJobsApplied,
                profile: stillCurrent.length === 0
                    ? { ...state.data.profile, primaryJobReviewedAt: null }
                    : state.data.profile,
            };

            // Remove from UI immediately — delete already succeeded on server
            dispatch({ type: 'REFRESH_DATA', payload: { data: updatedData } });
            try {
                dispatch({
                    type: 'SET_INTERNAL_STATE',
                    payload: {
                        key: 'model',
                        value: getProfileView({ data: updatedData, domain: state.domain, context: sessionState }),
                    },
                });
            } catch (viewError) {
                // Fallback: patch workExperience / jobsApplied on existing model
                console.error('Failed to rebuild profile view after delete', viewError);
                dispatch({
                    type: 'SET_INTERNAL_STATE',
                    payload: {
                        key: 'model',
                        value: {
                            ...model,
                            workExperience: (model.workExperience || []).filter(
                                (work: any) => String(work.id) !== String(workId)
                            ),
                        },
                    },
                });
            }

            sessionStorage.removeItem(`studentProfile-${model.id}`);
            syncProfileMarker(model.id).catch(() => undefined);

            Swal.fire({
                icon: 'success',
                title: 'Deleted!',
                text: 'Record deleted successfully.',
                timer: 1500,
                showConfirmButton: false,
            });
        } catch (error) {
            console.error('Failed to delete work experience', error);
            Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to delete. Please try again.' });
        } finally {
            setIsDeleting(false);
            setActionBtnStatus({ ...actionBtnStatus, [workId]: false });
        }
    };

    useEffect(() => {
        var tooltipTriggerList = [].slice.call(document.querySelectorAll('[data-bs-toggle="tooltip"]'));
        var tooltipList = tooltipTriggerList.map(function (tooltipTriggerEl) {
            return new Tooltip(tooltipTriggerEl);
        });
    });

    const isPeerReviewed = sessionState?.isPeerReviewed ?? false;
    const userType = sessionStorage.getItem('user-type-code');
    const isPersonate = sessionStorage.getItem('is-impersonated-session') == 'YES' ? true : false;
    // if (isPersonate != true && userType == 'ALUMNI' && isPeerReviewed != true) {
    //     window.location.href = `/alumni/welcome`;
    //     return;
    // }

    function getImage(workId: any, jobCategory: any, jobRoleCode: any) {
        const isFreelancing = jobCategory === 'SELF_EMPLOYED_AND_FREELANCING';
        const domainCode = isFreelancing ? 'FREELANCING_JOB_ROLE_CODE' : (jobCategory === 'TEACHING_AND_RESEARCH' ? 'INSTITUTE_DOMAIN' : 'COMPANY_CODE');
        const flexCol = getFlexColDomainValueForCode(isFreelancing ? jobRoleCode : workId, domainCode, state);
        console.log("flexCol",flexCol)
        const logoKey = isFreelancing ? 'img' : 'logo';
        return getCompanyImage(isEmpty(flexCol) ? '' : JSON.parse(flexCol)[logoKey] || '');
    }
    
    const jobCategoryPaths: Record<string, string> = {
        TEACHING_AND_RESEARCH: `/alumni/${model.id}/{work.id}/teaching-experience`,
        PROFESSIONAL_PRACTICE: `/alumni/${model.id}/{work.id}/professional-practice-experience`,
        DEFENCE: `/alumni/${model.id}/{work.id}/defence-experience`,
        IN_DEFENCE: `/alumni/${model.id}/{work.id}/defence-experience`,
        ENTREPRENEURSHIP: `/alumni/${model.id}/{work.id}/entrepreneurship`,
        SELF_EMPLOYED_AND_FREELANCING: `/alumni/${model.id}/{work.id}/freelancing-experience`,
        default: `/alumni/${model.id}/{work.id}/work`,
    };

    const workStatus = state.domain.get('WORK_STATUS_CODE')?.filter((status: any) =>
        status.code !== 'NOT_WORKING' && status.code !== 'HOME_MAKER'
    ) || [];

    const workTypeLabel = (jobCategory?: string) => {
        const code = jobCategory === 'DEFENCE' ? 'IN_DEFENCE' : jobCategory || 'WORKING_FOR_A_COMPANY';
        return getDomainValueForCode(code, 'WORK_STATUS_CODE', state);
    };
    
    const handleStatusChange = (status: string, event: React.MouseEvent) => {
        event.preventDefault();  
        setSelectedStatus(status);
        handleNavigate(status);
    };

    // const handleAddWork = () => {
    //     dispatch({ type: 'TOGGLE_MODAL_VISIBILITY', payload: { modalName: 'showWorkingStatusModal' } });
    // };

    const handleNavigate = (selectedStatus:any) => {
        const categoryPath = jobCategoryPaths[selectedStatus] || jobCategoryPaths['default'];
        const finalPath = categoryPath.replace('{work.id}', 'new');
        // const finalPath = (selectedStatus!=='ENTREPRENEURSHIP') ? categoryPath.replace('{work.id}', 'new') : categoryPath.replace('{work.id}', '');
        navigate(finalPath);
    };

    const handleDropdownToggle = () => {
        if ( !allowPerformAction() ) return;

        setIsDropdownOpen(!isDropdownOpen);
    };

    return (
        <div>
            <div className="workExperience-detail">
                <div className="blockHeader">
                    <div className="row g-2  align-items-center">
                        <div className="col">
                            {!model.isEditable && (
                                <div className="title">
                                    Companies{' '}
                                    <span>
                                        {`${model.firstName}`} {model.lastName ? `${model.lastName}` : ''}
                                    </span>{' '}
                                    has worked for
                                </div>
                            )}
                            {model.isEditable && <div className="title">My Work Experience</div>}
                        </div>
                        {model.isEditable && (
                            <div className="col-auto">
                              
                                 {/* <a role="button" onClick={handleAddWork} className="btn-add-section">
                                    <i></i> <span>ADD</span>
                                </a> */}
                                <div className="col-auto">
                                <div className="dropdown">
                                    <button
                                        className={`btn-add-section ${getActionElementClass()}`}
                                        type="button"
                                        id="dropdownMenuButton1"
                                        aria-expanded={isDropdownOpen ? 'true' : 'false'} 
                                        onClick={handleDropdownToggle} 
                                    ><i></i> <span>ADD</span></button>
                                    <ul className={`dropdown-menu ${isDropdownOpen ? 'show' : ''}`} aria-labelledby="dropdownMenuButton1">                                        {workStatus?.map((option: any) => (
                                            <li key={option.code}>
                                                <a 
                                                    className="dropdown-item" 
                                                    href="#"
                                                    onClick={(e) => handleStatusChange(option.code,e)}
                                                >{option.value}</a>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>

                            </div>
                        )}
                    </div>
                </div>
                <div className="workExperienceRow">
                    <div className="row g-4 g-md-5">
                        {model.workExperience.map((work: any) => (
                            <div key={`${work.id}-${work.companyName}`} className="col-md-6">
                                <div className="work-block">
                                    {/* added by Vp #1201 @18-11-24 */}
                                    <AdaptiveLogo
                                        className="icon workex-logo"
                                        imgClassName="img-fluid"
                                        src={getImage(work.companyId, work.jobCategory, work.jobRoleCode)}
                                        alt=""
                                        fallbackShape="square"
                                    />
                                    {/* end */}

                                    <div className="work-detail flex-row align-content-between">
                                        <div className="d-flex flex-column">
                                            <p className="work-designation">
                                                {formatDesignationDisplay(work.designation, state, { workingStatusCode: work.jobCategory })}
                                            </p>
                                            <div className='d-flex flex-wrap align-items-center'>
                                                <span className='flex-1'>
                                                    {/* {work.companyName}, {work.startYear} - {work.endYear} */}
                                                    {work.companyName}{work.startYear && (`, ${work.startYear}`)} {work.endYear && (` - ${work.endYear}`)}
                                                </span>
                                                {work?.isCampusPlacement && (  
                                                 <i className="icon-hat ms-2 blue-filter" data-bs-toggle="tooltip" data-bs-original-title="This was a campus placement" aria-label="This was a campus placement"></i>
                                                )}
                                            </div>
                                            {workTypeLabel(work.jobCategory) && (
                                                <span
                                                    className="badge text-bg-primary bg-opacity-25 text-primary-emphasis align-self-start mt-1"
                                                    style={{ fontSize: 11, lineHeight: '14px', fontWeight: 500, padding: '1px 6px' }}
                                                >
                                                    {workTypeLabel(work.jobCategory)}
                                                </span>
                                            )}
                                            {work.description && <ExpandableDescription text={work.description} />}
                                        </div>
                                        {model.isEditable && (
                                            <div className={`edit_section ${actionBtnStatus[work.id] ? 'open' : ''} ${isHidden ? 'hidden' : ''}`}>
                                                <button className="edit_section_toggle" onClick={() => {handleClick(work.id)}}></button>
                                                <div className="edit_section_inner">
                                                <a
                                                        role="button"
                                                        onClick={() => {
                                                            if ( !allowPerformAction() ) return;

                                                            const categoryPath = jobCategoryPaths[work.jobCategory] || jobCategoryPaths['default'];
                                                            const finalPath = categoryPath.replace('{work.id}', work.id);
                                                            navigate(finalPath);
                                                        }}
                                                        className={`edit_link ${getActionElementClass()}`}
                                                    >
                                                        Edit
                                                    </a>
                                                    <a
                                                        role="button"
                                                        onClick={() =>
                                                            handleDeleteWork(work.id, {
                                                                companyName: work.companyName,
                                                                designation: formatDesignationDisplay(work.designation, state, { workingStatusCode: work.jobCategory }),
                                                            })
                                                        }
                                                        className={`del_link ${getActionElementClass()}`}
                                                    >
                                                        Delete
                                                    </a>
                                                </div>
                                            </div>
                                        )}

                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
              {/* campus work ex #UAS-1201 by Vrinda @13-feb-2025 */}
              <div className="workExperience-detail border-d2d2d2 mb-4 mt-4">
                <div className="blockHeader">
                    <div className="row g-2  align-items-center">
                        <div className="col">
                            {!model.isEditable && (
                                <div className="title">
                                    Companies{' '}
                                    <span>
                                        {`${model.firstName}`} {model.lastName ? `${model.lastName}` : ''}
                                    </span>{' '}
                                    has worked for
                                </div>
                            )}
                            {model.isEditable && <div className="title">My Campus Placement Journey </div>}
                        </div>
                        {model.isEditable && (
                            <div className="col-auto">
                                <a role="button"
                                    className={`btn-add-section ${getActionElementClass()}`}
                                    onClick={() => {
                                        if (allowPerformAction()) {
                                            navigate(`/alumni/${model.id}/job-campus`);
                                        }
                                    }}>
                                    <i></i> <span>ADD</span>
                                </a>
                            </div>
                        )}
                    </div>
                </div>
                <div className="workExperienceRow">
                    <div className="row g-4 g-md-5">
                        {state?.data['jobsApplied'].map((work: any) => (
                            <div key={`${work.id}`} className="col-md-6">
                                <div className="work-block">
                                    {/* added by Vp #1201 @18-11-24 */}
                                    <AdaptiveLogo
                                        className="icon workex-logo"
                                        imgClassName="img-fluid"
                                        src={getImage(work.companyId, work.jobCategory, work.jobRoleCode)}
                                        alt=""
                                        fallbackShape="square"
                                    />
                                    {/* end */}

                                    <div className="work-detail flex-row align-content-between">
                                      <div className="d-flex flex-column">
                                                <p className="work-designation mb-0">
                                                {isEmpty(work.companyId) ? work.otherCompany : getDomainValueForCode(work.companyId, 'COMPANY_CODE', state)}
                                                </p>                                                
                                                <div className="row g-2">
                                                <div className="col-auto">
                                                <div className="got_offer">
                                                        {work.recordTypeCode === 'GOT_OFFER_DID_NOT_JOIN' ? (
                                                        <i className="icon-like text-green me-1"></i>
                                                        ) : null}
                                                        <span className="text-dark">
                                                        {getDomainValueForCode(work.recordTypeCode, 'COLLEGE_APPLICATION_RESULT_CODE', state) ?? ''}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        {model.isEditable && (
                                            <div className={`edit_section ${actionBtnStatus[work.id] ? 'open' : ''} ${isHidden ? 'hidden' : ''}`}>
                                                <button className="edit_section_toggle" onClick={() => {handleClick(work.id)}}></button>
                                                <div className="edit_section_inner">
                                                   <a
                                                        role="button"
                                                        onClick={() => {
                                                            if (allowPerformAction()) {
                                                                navigate(`/alumni/${model.id}/job-campus`);
                                                            }
                                                        }}
                                                        className={`edit_link ${getActionElementClass()}`}
                                                    >Edit</a>
                                                    <a
                                                        role="button"
                                                        onClick={() =>
                                                            handleDeleteWork(
                                                                work.id,
                                                                {
                                                                    companyName: isEmpty(work.companyId)
                                                                        ? work.otherCompany
                                                                        : getDomainValueForCode(work.companyId, 'COMPANY_CODE', state),
                                                                    designation: getDomainValueForCode(
                                                                        work.recordTypeCode,
                                                                        'COLLEGE_APPLICATION_RESULT_CODE',
                                                                        state
                                                                    ),
                                                                },
                                                                'campus'
                                                            )
                                                        }
                                                        className={`del_link ${getActionElementClass()}`}
                                                    >Delete</a>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
              </div>
                {/* end campus placement */}
                {/* Modal for selecting status */}
                           {/* <div
                                    id="send-email"
                                    className={`modal fade ${showFlag ? 'show' : ''}`} 
                                    tabIndex={-1}
                                    role="dialog"
                                    style={{ display: showFlag ? 'block' : 'none', backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
                                    <div className="modal-dialog modal-dialog-centered max-600 mx-auto">
                                        <div className="modal-content">
                                            <div className="modal-header p-4 height-70 border-0 bg-gray-f2">
                                                <h5 className="modal-title" id="staticBackdropLabel">
                                                    Add Work Experience
                                                </h5>
                                                <button
                                                    type="button"
                                                    className="btn-close btn-close-small"
                                                    onClick={() => {
                                                        dispatch({ type: 'TOGGLE_MODAL_VISIBILITY', payload: { modalName: 'showWorkingStatusModal' } });
                                                    }}
                                                    aria-label="Close"></button>
                                            </div>
                                            <div className="modal-body p-4">
                                                <form className="needs-validation" noValidate>
                                                    <div className="row g-4">
                                                        <div className="col-md-12">
                                                            <label className="form-label">Type of Experience: </label>
                                                            <div className="row g-2">
                                                                <div className="col">
                                                                    <div>
                                                                        <div className="row">
                                                                            {workStatus?.map((option: any, index: number) => (
                                                                                <div key={option.code} className="col-6 form-check">
                                                                                    <input
                                                                                        type="radio"
                                                                                        className="form-check-input"
                                                                                        id={`status-${option.code}`}
                                                                                        name="workingStatus"
                                                                                        value={option.code}
                                                                                        checked={selectedStatus === option.code}
                                                                                        onChange={(e) => handleStatusChange(e.target.value)}
                                                                                    />
                                                                                    <label className="form-check-label" htmlFor={`status-${option.code}`}>
                                                                                        {option.value}
                                                                                    </label>
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>

                                                        </div>
                                                    </div>
                                                </form>
                                            </div>
                                        </div>
                                    </div>
                                </div> */}
            {/* end od modal */}
            <WorkSimilarAlumni />
        </div>
    );
};

export default ProfileWorkExperience;
